import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { recruiterApi } from '../services/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { AlertCircle, ArrowRight, Calendar, CheckCircle2, FileText, Hash } from 'lucide-react'

interface Form {
  id: number
  uuid: string
  job_description: string | null
  questions: Array<any>
  created_at: string
  updated_at: string
}

interface FormQuestion {
  text?: string
  q_type?: 'MCQ' | 'Open'
  options?: string[]
  answer?: string
}

export default function FormOverviewPage() {
  const { formUuid } = useParams<{ formUuid: string }>()
  const navigate = useNavigate()
  const [forms, setForms] = useState<Form[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchForms = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await recruiterApi.getForms()

        if (response.status === 'success') {
          setForms(response.forms)
        } else {
          setError('שגיאה בטעינת הטפסים')
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'שגיאה בטעינת הטפסים')
      } finally {
        setIsLoading(false)
      }
    }

    fetchForms()
  }, [])

  const selectedForm = useMemo(() => {
    if (!formUuid) return null
    return forms.find((form) => form.uuid === formUuid) || null
  }, [forms, formUuid])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return dateString
    }
  }

  const normalizeQuestion = (question: any): FormQuestion => {
    if (typeof question === 'string') {
      return { text: question }
    }
    return question || {}
  }

  if (isLoading) {
    return (
      <div className="bg-background p-6 flex items-center justify-center min-h-[calc(100vh-96px)]">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-background p-6 space-y-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
        <Button variant="outline" onClick={() => navigate('/forms')} className="gap-2">
          <ArrowRight className="h-4 w-4" />
          חזור לרשימת שאלונים
        </Button>
      </div>
    )
  }

  if (!selectedForm) {
    return (
      <div className="bg-background p-6 space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-2">
              <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="text-lg font-medium">השאלון לא נמצא</p>
              <p className="text-sm text-muted-foreground">ייתכן שהשאלון נמחק או שהקישור שגוי.</p>
            </div>
          </CardContent>
        </Card>
        <Button variant="outline" onClick={() => navigate('/forms')} className="gap-2">
          <ArrowRight className="h-4 w-4" />
          חזור לרשימת שאלונים
        </Button>
      </div>
    )
  }

  return (
    <div className="bg-background p-6 h-[calc(100vh-96px)] overflow-hidden flex flex-col">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">סקירת שאלון</h1>
          <p className="text-muted-foreground mt-1">פרטים מלאים על השאלון שנבחר</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/forms')} className="gap-2">
          <ArrowRight className="h-4 w-4" />
          חזור לרשימת שאלונים
        </Button>
      </div>

      <div className="mt-6 flex-1 overflow-y-auto pl-2">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                פרטי שאלון
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <p className="text-sm font-medium">תיאור משרה</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {selectedForm.job_description || '—'}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="default">{selectedForm.questions?.length || 0} שאלות</Badge>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>תאריך יצירה: {formatDate(selectedForm.created_at)}</span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Hash className="h-4 w-4" />
                  <span>UUID מלא</span>
                </div>
                <code className="block w-full rounded bg-muted px-3 py-2 text-xs break-all">
                  {selectedForm.uuid}
                </code>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>רשימת שאלות מלאה</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedForm.questions?.length ? (
                <div className="space-y-3">
                  {selectedForm.questions.map((question, index) => {
                    const normalizedQuestion = normalizeQuestion(question)
                    const questionText = normalizedQuestion.text || JSON.stringify(question, null, 2)
                    const questionOptions = normalizedQuestion.options || []
                    const correctAnswer = normalizedQuestion.answer
                    const questionType = normalizedQuestion.q_type

                    return (
                      <div key={index} className="rounded-md border bg-card p-4">
                        <p className="text-sm font-medium mb-1">שאלה {index + 1}</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                          {questionText}
                        </p>
                        {questionType && (
                          <p className="mt-2 text-xs text-muted-foreground">סוג שאלה: {questionType}</p>
                        )}

                        {questionType === 'MCQ' && questionOptions.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-sm font-medium">אפשרויות תשובה:</p>
                            <div className="space-y-1">
                              {questionOptions.map((option, optionIndex) => {
                                const isCorrect = !!correctAnswer && option === correctAnswer
                                return (
                                  <div
                                    key={`${index}-${optionIndex}`}
                                    className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
                                      isCorrect
                                        ? 'border-green-200 bg-green-50 text-green-800'
                                        : 'border-border bg-background text-muted-foreground'
                                    }`}
                                  >
                                    <span className="break-words">{option}</span>
                                    {isCorrect && (
                                      <span className="flex items-center gap-1 text-xs font-medium whitespace-nowrap">
                                        <CheckCircle2 className="h-4 w-4" />
                                        תשובה נכונה
                                      </span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {questionType === 'Open' && correctAnswer && (
                          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                            <span className="font-medium">תשובה צפויה:</span> {correctAnswer}
                          </div>
                        )}

                        {!questionType && questionOptions.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-sm font-medium">אפשרויות תשובה:</p>
                            <div className="space-y-1">
                              {questionOptions.map((option, optionIndex) => {
                                const isCorrect = !!correctAnswer && option === correctAnswer
                                return (
                                  <div
                                    key={`${index}-fallback-${optionIndex}`}
                                    className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
                                      isCorrect
                                        ? 'border-green-200 bg-green-50 text-green-800'
                                        : 'border-border bg-background text-muted-foreground'
                                    }`}
                                  >
                                    <span className="break-words">{option}</span>
                                    {isCorrect && (
                                      <span className="flex items-center gap-1 text-xs font-medium whitespace-nowrap">
                                        <CheckCircle2 className="h-4 w-4" />
                                        תשובה נכונה
                                      </span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {correctAnswer && questionOptions.length === 0 && questionType !== 'Open' && (
                          <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                            <span className="font-medium">תשובה נכונה:</span> {correctAnswer}
                          </div>
                        )}
                      </div>
                    )
                  })}
            </div>
              ) : (
                <p className="text-sm text-muted-foreground">אין שאלות להצגה בשאלון זה.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
